import { motion } from 'framer-motion';
import { SimpleLayout, ST } from '../components/SimpleLayout';
import {
  Bell,
  Calendar,
  Car,
  Check,
  Filter,
  Lock,
  MapPin,
  Shield,
  Zap,
} from 'lucide-react';

function Pill({
  children,
  tone = 'default',
  style = {},
}: {
  children: React.ReactNode;
  tone?: 'default' | 'accent' | 'success' | 'muted';
  style?: React.CSSProperties;
}) {
  const tones = {
    default: { background: ST.primarySoft, color: ST.primary },
    accent: { background: ST.accentSoft, color: ST.accentDeep },
    success: { background: ST.successSoft, color: ST.success },
    muted: { background: ST.surfaceAlt, color: ST.textMuted, border: `1px solid ${ST.border}` },
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.1,
        ...tones[tone],
        ...style,
      }}
    >
      {children}
    </span>
  );
}

function PhoneMockup() {
  return (
    <div
      style={{
        width: 280,
        height: 580,
        borderRadius: 40,
        background: ST.bg,
        boxShadow: '0 40px 80px rgba(20,30,55,0.18), 0 0 0 1px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 90,
          height: 26,
          borderRadius: 18,
          background: '#000',
          zIndex: 10,
        }}
      />
      <div
        style={{
          height: 44,
          paddingTop: 14,
          paddingLeft: 22,
          paddingRight: 22,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: ST.text }}>9:41</span>
        <div
          style={{
            width: 14,
            height: 8,
            borderRadius: 2,
            border: `1.5px solid ${ST.text}`,
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 1,
              left: 1,
              right: 2,
              bottom: 1,
              background: ST.text,
              borderRadius: 1,
            }}
          />
        </div>
      </div>
      <div style={{ padding: '4px 14px 0', overflow: 'hidden' }}>
        <div
          style={{
            borderRadius: 18,
            padding: 14,
            background: `linear-gradient(160deg, ${ST.heroStart} 0%, ${ST.heroEnd} 100%)`,
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: -30,
              right: -30,
              width: 100,
              height: 100,
              borderRadius: 999,
              background: 'rgba(245,158,11,0.25)',
              filter: 'blur(20px)',
            }}
          />
          <div style={{ fontSize: 10, opacity: 0.7 }}>Cześć,</div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.4 }}>Marcin</div>
          <div
            style={{
              marginTop: 10,
              padding: 10,
              borderRadius: 12,
              background: 'rgba(255,255,255,0.08)',
              fontSize: 11,
              lineHeight: 1.5,
            }}
          >
            <span style={{ opacity: 0.85 }}>Monitorujemy egzamin w </span>
            <b>WORD Wrocław</b>
          </div>
        </div>
        <div
          style={{
            marginTop: 10,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
          }}
        >
          {[
            { label: 'Teoria', value: '24', tone: ST.primary, bg: ST.primarySoft },
            { label: 'Praktyka', value: '8', tone: ST.success, bg: ST.successSoft },
          ].map((c) => (
            <div
              key={c.label}
              style={{
                background: ST.surface,
                borderRadius: 14,
                padding: 10,
                border: `1px solid ${ST.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: ST.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {c.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: ST.text, marginTop: 2 }}>
                {c.value}
              </div>
              <div style={{ display: 'flex', gap: 2, marginTop: 6, alignItems: 'flex-end', height: 20 }}>
                {[0.4, 0.7, 0.3, 0.9, 0.55, 0.8, 0.5].map((h, i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      height: `${h * 100}%`,
                      borderRadius: 2,
                      background: c.bg,
                      borderTop: `2px solid ${c.tone}`,
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 10,
            fontSize: 9,
            fontWeight: 700,
            color: ST.textMuted,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            paddingLeft: 2,
            marginBottom: 6,
          }}
        >
          Powiadomienia
        </div>
        {[
          {
            Icon: Bell,
            t: 'Nowy termin w WORD Wrocław',
            s: '12 maja · 09:30 · 2 min temu',
            cs: ST.accentSoft,
            c: ST.accentDeep,
          },
          {
            Icon: Calendar,
            t: 'Wolne miejsca — teoria',
            s: '15 maja · 11:00',
            cs: ST.primarySoft,
            c: ST.primary,
          },
        ].map((r, i) => (
          <div
            key={i}
            style={{
              background: ST.surface,
              borderRadius: 12,
              padding: 10,
              border: `1px solid ${ST.border}`,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 6,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: r.cs,
                color: r.c,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <r.Icon size={14} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600 }}>{r.t}</div>
              <div style={{ fontSize: 10, color: ST.textMuted }}>{r.s}</div>
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: ST.surface,
          borderTop: `1px solid ${ST.border}`,
          display: 'flex',
          justifyContent: 'space-around',
          paddingBottom: 16,
          paddingTop: 8,
        }}
      >
        {[
          { label: 'Start', color: ST.primary, active: true },
          { label: 'Terminy', color: ST.textMuted, active: false },
          { label: 'Profil', color: ST.textMuted, active: false },
        ].map((tab) => (
          <div
            key={tab.label}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              color: tab.color,
            }}
          >
            <div
              style={{
                width: 16,
                height: 2,
                borderRadius: 999,
                background: tab.active ? ST.primary : 'transparent',
                marginBottom: 2,
              }}
            />
            <span style={{ fontSize: 9, fontWeight: 600 }}>{tab.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section
      style={{
        background: `radial-gradient(ellipse 80% 60% at 50% -10%, ${ST.primarySoft} 0%, ${ST.bg} 60%)`,
        paddingTop: 120,
        paddingBottom: 80,
      }}
    >
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 24px' }}>
        <div
          className="mc-hero-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,1fr)',
            gap: 48,
            alignItems: 'center',
          }}
        >
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 999,
                background: ST.surface,
                border: `1px solid ${ST.border}`,
                fontSize: 13,
                color: ST.textMuted,
                fontWeight: 500,
                marginBottom: 24,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: ST.success,
                  display: 'inline-block',
                }}
              />
              Sprawdzamy dostępne terminy co minutę
            </div>
            <h1
              style={{
                fontSize: 'clamp(40px, 5vw, 64px)',
                lineHeight: 1.05,
                fontWeight: 800,
                letterSpacing: -2,
                margin: '0 0 20px 0',
                color: ST.text,
              }}
            >
              Łap wolne terminy egzaminu,{' '}
              <span style={{ color: ST.accent }}>zanim znikną</span>.
            </h1>
            <p
              style={{
                fontSize: 18,
                color: ST.textMuted,
                lineHeight: 1.6,
                margin: '0 0 32px 0',
                maxWidth: 520,
              }}
            >
              Marmo Car monitoruje 49 polskich WORD-ów 24/7 i wysyła Ci powiadomienie,
              gdy tylko pojawi się termin pasujący do Twoich preferencji.
            </p>
            <div style={{ display: 'flex', gap: 20, fontSize: 13, color: ST.textMuted, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Zap size={15} color={ST.accent} /> Powiadomienia w sekundach
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MapPin size={15} color={ST.primary} /> 49 WORD-ów
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Shield size={15} color={ST.success} /> RODO &amp; Google Sign-In
              </span>
            </div>
          </div>
          <div
            className="mc-hero-phone"
            style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}
          >
            <div
              style={{
                position: 'absolute',
                inset: -40,
                zIndex: 0,
                background: `radial-gradient(circle at 50% 50%, ${ST.accentSoft} 0%, transparent 60%)`,
                filter: 'blur(40px)',
              }}
            />
            <div
              style={{
                transform: 'scale(0.92)',
                transformOrigin: 'top center',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <PhoneMockup />
            </div>
            <div
              style={{
                position: 'absolute',
                top: 60,
                right: -20,
                zIndex: 2,
                background: ST.surface,
                padding: 14,
                borderRadius: 14,
                boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.10)',
                border: `1px solid ${ST.border}`,
                width: 220,
                transform: 'rotate(3deg)',
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: ST.accentSoft,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Bell size={16} color={ST.accentDeep} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: ST.text }}>
                    Nowy termin!
                  </div>
                  <div style={{ fontSize: 11, color: ST.textMuted, marginTop: 2 }}>
                    Praktyka · 12 maja, 09:30 · WORD Wrocław
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BentoCard({
  title,
  desc,
  children,
  span = 1,
}: {
  title: string;
  desc: string;
  children: React.ReactNode;
  span?: number;
}) {
  return (
    <div
      className={`mc-bento-card mc-bento-span-${span}`}
      style={{
        background: ST.surface,
        border: `1px solid ${ST.border}`,
        borderRadius: 20,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        gridColumn: `span ${span}`,
        minHeight: 280,
      }}
    >
      <div>
        <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: ST.text, letterSpacing: -0.3 }}>
          {title}
        </h3>
        <p style={{ fontSize: 14, color: ST.textMuted, lineHeight: 1.55, margin: '6px 0 0 0' }}>
          {desc}
        </p>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}

function BentoMonitoring() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ position: 'relative', width: 80, height: 80 }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 999,
            background: ST.successSoft,
            animation: 'mcPulse 2s ease-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 18,
            borderRadius: 999,
            background: ST.success,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          24/7
        </div>
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, color: ST.text, letterSpacing: -0.8 }}>
          49 WORD
        </div>
        <div style={{ fontSize: 13, color: ST.textMuted }}>sprawdzanych co 60 s</div>
      </div>
    </div>
  );
}

function BentoFilters() {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
      <Pill tone="accent">Praktyka</Pill>
      <Pill tone="muted">Teoria</Pill>
      <Pill tone="success">Wrocław</Pill>
      <Pill tone="muted">Warszawa</Pill>
      <Pill tone="muted">Kraków</Pill>
      <Pill tone="muted">Pon–Pt</Pill>
      <Pill tone="default">08:00–14:00</Pill>
      <Pill tone="muted">Weekend</Pill>
    </div>
  );
}

function BentoNotification() {
  return (
    <div
      style={{
        background: ST.surfaceSunken,
        borderRadius: 16,
        padding: 12,
        width: '100%',
        maxWidth: 280,
        border: `1px solid ${ST.border}`,
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: `linear-gradient(135deg, ${ST.heroStart}, ${ST.heroEnd})`,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Car size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: ST.text }}>Marmo Car</span>
            <span style={{ fontSize: 10, color: ST.textSubtle }}>teraz</span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: ST.text, marginTop: 2 }}>
            Nowy termin w WORD Wrocław
          </div>
          <div style={{ fontSize: 12, color: ST.textMuted, marginTop: 2 }}>
            Praktyka B · 12 maja · 09:30
          </div>
        </div>
      </div>
    </div>
  );
}

function BentoWeeklyPicker() {
  const days = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];
  const slots = [3, 1, 5, 0, 2, 4, 0];
  return (
    <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
      {days.map((d, i) => {
        const has = slots[i] > 0;
        return (
          <div
            key={d}
            style={{
              background: has ? ST.primarySoft : ST.surfaceAlt,
              border: `1px solid ${has ? ST.primary : ST.border}`,
              borderRadius: 10,
              padding: '8px 4px',
              textAlign: 'center',
              color: has ? ST.primary : ST.textSubtle,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>{d}</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{slots[i]}</div>
          </div>
        );
      })}
    </div>
  );
}

function BentoSecurity() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      {[
        { Icon: Shield, t: 'Zgodne z RODO', s: 'Polityka prywatności v1.1' },
        { Icon: Lock, t: 'Google Sign-In', s: 'Bez haseł — OAuth 2.0' },
        { Icon: Check, t: 'Szyfrowanie TLS', s: 'Cała transmisja po HTTPS' },
      ].map((r) => (
        <div
          key={r.t}
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            background: ST.surfaceAlt,
            border: `1px solid ${ST.border}`,
            borderRadius: 12,
            padding: 10,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: ST.successSoft,
              color: ST.success,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <r.Icon size={16} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: ST.text }}>{r.t}</div>
            <div style={{ fontSize: 12, color: ST.textMuted }}>{r.s}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BentoBarChart() {
  const bars = [0.3, 0.55, 0.4, 0.85, 0.6, 0.92, 0.45];
  const labels = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];
  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 8,
          alignItems: 'end',
          height: 120,
        }}
      >
        {bars.map((h, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              height: '100%',
              justifyContent: 'flex-end',
            }}
          >
            <motion.div
              initial={{ scaleY: 0, opacity: 0 }}
              whileInView={{ scaleY: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 0.7,
                delay: 0.15 + i * 0.09,
                ease: [0.33, 1, 0.68, 1],
              }}
              style={{
                width: '100%',
                height: `${h * 100}%`,
                borderRadius: 8,
                background: `linear-gradient(180deg, ${ST.accent}, ${ST.accentDeep})`,
                transformOrigin: 'bottom center',
                boxShadow: `0 2px 8px ${ST.accentSoft}`,
              }}
            />
            <span style={{ fontSize: 11, color: ST.textMuted, fontWeight: 600 }}>{labels[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Features() {
  return (
    <section id="funkcje" style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Pill tone="default" style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Funkcje
          </Pill>
          <h2
            style={{
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: 800,
              letterSpacing: -1.4,
              margin: '16px 0 12px 0',
              color: ST.text,
            }}
          >
            Wszystko, co potrzebne, aby zdążyć
          </h2>
          <p style={{ fontSize: 17, color: ST.textMuted, maxWidth: 620, margin: '0 auto' }}>
            Pełna kontrola nad poszukiwaniem terminu — od wyboru WORD-u po powiadomienie
            o nowym slocie.
          </p>
        </div>
        <div
          className="mc-bento-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
          }}
        >
          <BentoCard
            title="Monitoring 24/7"
            desc="Sprawdzamy każdy z 49 WORD-ów co 60 sekund, nawet w środku nocy."
          >
            <BentoMonitoring />
          </BentoCard>
          <BentoCard
            title="Inteligentne filtry"
            desc="Wybierz rodzaj egzaminu, miasto i godziny — dostaniesz tylko to, co Cię interesuje."
            span={2}
          >
            <BentoFilters />
          </BentoCard>
          <BentoCard
            title="Powiadomienia push"
            desc="Alert na telefonie w sekundę po pojawieniu się nowego terminu."
            span={2}
          >
            <BentoNotification />
          </BentoCard>
          <BentoCard
            title="Kalendarz tygodniowy"
            desc="Zobacz dostępność w każdym dniu jednym spojrzeniem."
          >
            <BentoWeeklyPicker />
          </BentoCard>
          <BentoCard
            title="Statystyki dostępności"
            desc="Wykresy pokazują najlepsze dni i godziny na rezerwację."
          >
            <BentoBarChart />
          </BentoCard>
          <BentoCard
            title="Bezpieczeństwo"
            desc="Logowanie Google, szyfrowanie TLS, brak haseł u nas — pełna zgodność z RODO."
            span={2}
          >
            <BentoSecurity />
          </BentoCard>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: '01',
      Icon: MapPin,
      t: 'Wybierz WORD',
      d: 'Zaznacz jeden lub kilka WORD-ów w okolicy, w której chcesz zdawać.',
    },
    {
      n: '02',
      Icon: Filter,
      t: 'Ustaw preferencje',
      d: 'Teoria czy praktyka, dni i godziny — wszystko w 30 sekund.',
    },
    {
      n: '03',
      Icon: Bell,
      t: 'Odbierz powiadomienie',
      d: 'Gdy pojawi się pasujący termin, dostaniesz powiadomienie push.',
    },
  ];
  return (
    <section
      id="jak-dziala"
      style={{
        padding: '80px 24px',
        background: ST.surface,
        borderTop: `1px solid ${ST.border}`,
        borderBottom: `1px solid ${ST.border}`,
      }}
    >
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Pill tone="accent" style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Jak to działa
          </Pill>
          <h2
            style={{
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: 800,
              letterSpacing: -1.4,
              margin: '16px 0 12px 0',
              color: ST.text,
            }}
          >
            Trzy kroki do nowego terminu
          </h2>
        </div>
        <div
          className="mc-steps-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 20,
          }}
        >
          {steps.map((s) => (
            <div
              key={s.n}
              style={{
                background: ST.bg,
                border: `1px solid ${ST.border}`,
                borderRadius: 18,
                padding: 28,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 18,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: ST.primarySoft,
                    color: ST.primary,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <s.Icon size={22} />
                </div>
                <span
                  style={{
                    fontSize: 36,
                    fontWeight: 800,
                    color: ST.borderStrong,
                    letterSpacing: -1.5,
                  }}
                >
                  {s.n}
                </span>
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px 0', color: ST.text }}>
                {s.t}
              </h3>
              <p style={{ fontSize: 14, color: ST.textMuted, margin: 0, lineHeight: 1.6 }}>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const items = [
    { n: '49', l: 'WORD-ów monitorowanych' },
    { n: '60 s', l: 'cykl sprawdzania' },
    { n: '24/7', l: 'bez przerwy, także nocą' },
    { n: 'Push', l: 'natychmiastowe alerty' },
  ];
  return (
    <section
      id="bezpieczenstwo"
      style={{
        padding: '64px 24px',
        background: `linear-gradient(135deg, ${ST.heroStart}, ${ST.heroEnd})`,
        color: '#fff',
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 24,
          textAlign: 'center',
        }}
        className="mc-stats-grid"
      >
        {items.map((it) => (
          <div key={it.l}>
            <div
              style={{
                fontSize: 'clamp(36px, 4vw, 52px)',
                fontWeight: 800,
                color: ST.accent,
                letterSpacing: -1.5,
              }}
            >
              {it.n}
            </div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>{it.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function MarmoCarLanding() {
  return (
    <SimpleLayout>
      <style>{`
        @keyframes mcPulse {
          0% { transform: scale(0.6); opacity: 0.9; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @media (max-width: 880px) {
          .mc-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .mc-hero-phone { transform: scale(0.85); }
          .mc-bento-grid { grid-template-columns: 1fr !important; }
          .mc-bento-card { grid-column: span 1 !important; min-height: 220px !important; }
          .mc-steps-grid { grid-template-columns: 1fr !important; }
          .mc-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 32px !important; }
        }
      `}</style>
      <Hero />
      <Features />
      <HowItWorks />
      <Stats />
    </SimpleLayout>
  );
}
